/**
 * RSA-OAEP-SHA256 encryption of the dev-login envelope using the Web
 * Crypto API. Produces the base64 payload the backend expects in
 * `DevLoginRequestDto.encryptedToken`.
 *
 * Contract:
 *   - Scheme:         RSA-OAEP
 *   - Hash / MGF1:    SHA-256
 *   - Label:          none (empty)
 *   - Public key:     SubjectPublicKeyInfo (SPKI) DER, base64, no PEM
 *                     headers, no line breaks
 *   - Plaintext:      UTF-8 bytes of JSON `{ token, timestamp }` where
 *                     `timestamp` is Unix epoch **seconds** (NOT ms),
 *                     integer, UTC
 *   - Freshness:      backend enforces `|serverUtcNow - timestamp| <= 30s`
 *                     — outside the window the login is rejected with
 *                     the same neutral 401 as any other bad input. The
 *                     browser's wall clock is expected to be within a
 *                     few seconds of server UTC; NTP drift is fine.
 *   - Wire encoding:  plain base64 (NOT base64url), padding preserved
 *
 * The public key is loaded at runtime from `/assets/config.json` under
 * `devAuth.publicKey` and exposed via
 * `RuntimeConfigService.devAuthPublicKey()` (public half of the dev JWT
 * key pair; safe to commit, per-environment, rotated in lockstep with
 * the backend's `DevAccess:Jwt` config — see
 * `docs/deployment/runtime-config.md`).
 *
 * Replay protection: each encryption uses fresh OAEP randomness AND a
 * fresh timestamp, so a captured ciphertext becomes useless ~30 seconds
 * after the real developer submits it.
 *
 * Kept Angular-free + DI-free so it can be unit-tested against known
 * vectors without bootstrapping a test module.
 */

interface DevLoginEnvelope {
  token: string;
  timestamp: number;
}

/** Base64 (no line breaks) → ArrayBuffer. */
function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let byteIndex = 0; byteIndex < binary.length; byteIndex++) {
    bytes[byteIndex] = binary.charCodeAt(byteIndex);
  }
  return bytes.buffer;
}

/** ArrayBuffer → plain base64 (no line breaks, no base64url). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let byteIndex = 0; byteIndex < bytes.byteLength; byteIndex++) {
    binary += String.fromCharCode(bytes[byteIndex]);
  }
  return btoa(binary);
}

/**
 * Wraps `plaintextToken` in a `{ token, timestamp }` envelope, encrypts
 * the UTF-8 JSON bytes with the SPKI-encoded RSA public key, and returns
 * the base64 ciphertext ready to drop into the dev-login request body as
 * `encryptedToken`.
 *
 * The `timestamp` field is `Math.floor(Date.now() / 1000)` — Unix seconds.
 * Sending milliseconds here would land ~50 years in the future and 401
 * every time; the seconds-cast is deliberate.
 *
 * Throws (the caller should `catchError` and surface a neutral failure)
 * when:
 *   - `publicKeyBase64` is empty / malformed / not SPKI
 *   - the envelope exceeds 190 bytes (2048-bit RSA + SHA-256 cap) — in
 *     practice only possible if the shared dev token itself is enormous
 *   - the browser lacks `crypto.subtle` (should not happen in modern
 *     secure-context browsers)
 *
 * Each call produces a different ciphertext (OAEP randomness + fresh
 * timestamp). Do NOT memoise the return value.
 */
export async function encryptDevToken(
  plaintextToken: string,
  publicKeyBase64: string
): Promise<string> {
  if (!publicKeyBase64) {
    throw new Error('dev auth public key is not configured');
  }
  if (!globalThis.crypto?.subtle) {
    throw new Error('Web Crypto API is not available in this environment');
  }

  const keyDer = base64ToArrayBuffer(publicKeyBase64);
  const publicKey = await crypto.subtle.importKey(
    'spki',
    keyDer,
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt']
  );

  const envelope: DevLoginEnvelope = {
    token: plaintextToken,
    timestamp: Math.floor(Date.now() / 1000),
  };
  const envelopeJson = JSON.stringify(envelope);
  const plaintextBytes = new TextEncoder().encode(envelopeJson);

  const ciphertextBuf = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    plaintextBytes
  );

  return arrayBufferToBase64(ciphertextBuf);
}
